import { useState, useEffect } from 'react';
import {
  Users,
  Plus,
  Send,
  RefreshCw,
  AlertCircle,
  Loader2,
  CheckCircle2,
  CalendarDays,
  UserPlus,
} from 'lucide-react';
import { cn } from '../../../shared';
import {
  JoinerSelect,
  FINNieRow,
  QueueRow,
  type FINNieRowItem,
} from '../components/G2KComponents';
import {
  fetchNewJoiners,
  sendG2KsToScheduler,
  type NewJoiner,
  type G2KEntry,
} from '../services/onboardingApi';

// ---------------------------------------------------------------------------
// Tab bar
// ---------------------------------------------------------------------------

type Tab = 'g2k';

const TABS: { id: Tab; label: string; icon: React.ElementType }[] = [
  { id: 'g2k', label: 'Get to Knows (G2K)', icon: Users },
];

// ---------------------------------------------------------------------------
// G2K Tab
// ---------------------------------------------------------------------------

function makeRow(): FINNieRowItem {
  return { rowId: crypto.randomUUID(), employee: null, duration: 30 };
}

function G2KTab() {
  const [joiners, setJoiners] = useState<NewJoiner[]>([]);
  const [loadingJoiners, setLoadingJoiners] = useState(true);
  const [joinersError, setJoinersError] = useState<string | null>(null);

  // Builder state
  const [selectedJoiner, setSelectedJoiner] = useState<NewJoiner | null>(null);
  const [finnieRows, setFINNieRows] = useState<FINNieRowItem[]>([makeRow()]);

  // Queue
  const [queue, setQueue] = useState<G2KEntry[]>([]);
  const [searchWindowDays, setSearchWindowDays] = useState(14);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [sendSuccess, setSendSuccess] = useState(false);

  const usedJoinerIds = new Set(queue.map(e => e.joiner.id));
  const selectedEmployeeEmails = finnieRows.map(r => r.employee?.email ?? '').filter(Boolean);
  const filledRows = finnieRows.filter(r => r.employee !== null);
  const canAddToQueue = selectedJoiner !== null && filledRows.length > 0;

  useEffect(() => { void loadJoiners(); }, []);

  async function loadJoiners() {
    setLoadingJoiners(true);
    setJoinersError(null);
    try {
      const data = await fetchNewJoiners();
      setJoiners(data.joiners);
    } catch (err) {
      setJoinersError(err instanceof Error ? err.message : 'Failed to load new joiners.');
    } finally {
      setLoadingJoiners(false);
    }
  }

  function updateRow(rowId: string, updated: FINNieRowItem) {
    setFINNieRows(rows => rows.map(r => r.rowId === rowId ? updated : r));
  }

  function removeRow(rowId: string) {
    setFINNieRows(rows => rows.filter(r => r.rowId !== rowId));
  }

  function addRow() {
    setFINNieRows(rows => [...rows, makeRow()]);
  }

  function addToQueue() {
    if (!canAddToQueue) return;
    const newEntries: G2KEntry[] = filledRows.map(row => ({
      id: `${selectedJoiner!.id}-${row.employee!.uid}-${Date.now()}-${row.rowId}`,
      joiner: selectedJoiner!,
      employee: row.employee!,
      durationMinutes: row.duration,
    }));
    setQueue(q => [...q, ...newEntries]);
    setSelectedJoiner(null);
    setFINNieRows([makeRow()]);
  }

  function removeFromQueue(id: string) {
    setQueue(q => q.filter(e => e.id !== id));
  }

  async function handleSend() {
    if (queue.length === 0) return;
    setSending(true);
    setSendError(null);
    setSendSuccess(false);
    try {
      const submitted = queue;
      const { sent, failed, unconfirmed } = await sendG2KsToScheduler(submitted, searchWindowDays);
      // Only joiners Starbase refused stay queued; entries added while sending are kept too.
      const retryable = new Set(failed.map(f => f.email));
      const done = new Set(submitted.filter(e => !retryable.has(e.joiner.email)).map(e => e.id));
      setQueue(q => q.filter(e => !done.has(e.id)));
      if (failed.length === 0 && unconfirmed.length === 0) {
        setSendSuccess(true);
      } else {
        const label = (j: { name: string; email: string }) => j.name || j.email;
        const parts = [
          sent.length > 0 && `Sent for ${sent.map(label).join(', ')}.`,
          unconfirmed.length > 0 &&
            `No answer for ${unconfirmed.map(label).join(', ')} — check the scheduler's runs before sending them again.`,
          failed.length > 0 && `Still queued: ${failed.map(f => `${label(f)} (${f.error})`).join('; ')}.`,
        ];
        setSendError(parts.filter(Boolean).join(' '));
      }
    } catch (err) {
      setSendError(err instanceof Error ? err.message : 'Failed to send G2Ks.');
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="space-y-6 max-w-2xl">
      {/* New joiners status */}
      {loadingJoiners ? (
        <div className="flex items-center gap-2 text-sm text-zinc-500">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading new joiners…
        </div>
      ) : joinersError ? (
        <div className="flex items-start gap-3 rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
          <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
          <div className="flex-1">
            {joinersError}
            <button type="button" onClick={loadJoiners} className="ml-3 font-semibold underline hover:no-underline">Retry</button>
          </div>
        </div>
      ) : joiners.length === 0 ? (
        <div className="flex items-center gap-2 rounded-xl border border-zinc-100 bg-zinc-50 px-4 py-3 text-sm text-zinc-500">
          <CalendarDays className="h-4 w-4 shrink-0" />
          No new joiners with a hire date in the next/past 15 days found.
          <button type="button" onClick={loadJoiners} className="ml-1 text-blue-600 hover:underline font-medium">Refresh</button>
        </div>
      ) : (
        <div className="flex items-center gap-2 text-sm text-zinc-500">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-semibold text-emerald-700">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
            {joiners.length} new joiner{joiners.length !== 1 ? 's' : ''} in cohort
          </span>
          <button type="button" onClick={loadJoiners} className="text-zinc-400 hover:text-zinc-700 transition-colors" title="Refresh">
            <RefreshCw className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      {/* Builder card */}
      <div className="rounded-2xl border border-zinc-100 bg-zinc-50 p-5 space-y-4">
        <h3 className="text-sm font-semibold text-zinc-700">Schedule G2Ks</h3>

        {/* Joiner picker */}
        <div className="space-y-1">
          <label className="text-xs font-medium text-zinc-500">New Joiner</label>
          <JoinerSelect
            joiners={joiners}
            selected={selectedJoiner}
            onChange={setSelectedJoiner}
            usedIds={usedJoinerIds}
          />
        </div>

        {/* FINNie rows */}
        <div className="space-y-1">
          <label className="text-xs font-medium text-zinc-500">FINNies to meet</label>
          <div className="space-y-2">
            {finnieRows.map(row => (
              <FINNieRow
                key={row.rowId}
                row={row}
                selectedEmails={selectedEmployeeEmails}
                onChange={updated => updateRow(row.rowId, updated)}
                onRemove={() => removeRow(row.rowId)}
                canRemove={finnieRows.length > 1}
              />
            ))}
          </div>

          <button
            type="button"
            onClick={addRow}
            className="mt-1 flex items-center gap-1.5 text-xs font-medium text-blue-600 hover:text-blue-800 transition-colors"
          >
            <UserPlus className="h-3.5 w-3.5" />
            Add another FINNie
          </button>
        </div>

        <button
          type="button"
          onClick={addToQueue}
          disabled={!canAddToQueue}
          className={cn(
            'flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold transition-colors',
            canAddToQueue
              ? 'bg-zinc-900 text-white hover:bg-zinc-700'
              : 'bg-zinc-200 text-zinc-400 cursor-not-allowed',
          )}
        >
          <Plus className="h-4 w-4" />
          {filledRows.length > 0
            ? `Add ${filledRows.length} G2K${filledRows.length !== 1 ? 's' : ''} to queue`
            : 'Add to queue'}
        </button>
      </div>

      {/* Queue */}
      {queue.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-zinc-700">
            Scheduled G2Ks
            <span className="ml-2 inline-flex items-center justify-center h-5 w-5 rounded-full bg-zinc-200 text-xs font-bold text-zinc-600">
              {queue.length}
            </span>
          </h3>

          <div className="space-y-2">
            {queue.map(entry => (
              <QueueRow key={entry.id} entry={entry} onRemove={() => removeFromQueue(entry.id)} />
            ))}
          </div>

          {/* Search window */}
          <div className="flex items-center gap-3 rounded-xl border border-zinc-100 bg-zinc-50 px-4 py-3">
            <CalendarDays className="h-4 w-4 text-zinc-400 shrink-0" />
            <label className="text-sm text-zinc-600 font-medium shrink-0">Search window</label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                min={7}
                max={60}
                value={searchWindowDays}
                onChange={e => setSearchWindowDays(Math.max(1, parseInt(e.target.value) || 14))}
                className="w-16 rounded-lg border border-zinc-200 bg-white px-2 py-1 text-sm text-center font-semibold text-zinc-900 focus:outline-none focus:border-blue-400"
              />
              <span className="text-sm text-zinc-500">days</span>
            </div>
            <span className="text-xs text-zinc-400">The agent will look for available slots within this window.</span>
          </div>

          {sendSuccess && (
            <div className="flex items-center gap-3 rounded-xl border border-emerald-100 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
              <CheckCircle2 className="h-4 w-4 shrink-0" />
              All G2Ks sent! The scheduling agent will take care of the rest.
            </div>
          )}

          {sendError && (
            <div className="flex items-start gap-3 rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
              <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
              {sendError}
            </div>
          )}

          <button
            type="button"
            onClick={handleSend}
            disabled={sending}
            className={cn(
              'flex items-center gap-2 rounded-xl px-5 py-2.5 text-sm font-semibold transition-colors',
              sending ? 'bg-zinc-200 text-zinc-400 cursor-not-allowed' : 'bg-blue-600 text-white hover:bg-blue-700',
            )}
          >
            {sending ? (
              <><Loader2 className="h-4 w-4 animate-spin" />Sending to scheduling agent…</>
            ) : (
              <><Send className="h-4 w-4" />Send {queue.length} G2K{queue.length !== 1 ? 's' : ''} to scheduling agent</>
            )}
          </button>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main view
// ---------------------------------------------------------------------------

export default function OnboardingSchedulingView() {
  const [activeTab, setActiveTab] = useState<Tab>('g2k');

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-bold text-zinc-900">Onboarding Scheduling</h1>
        <p className="mt-1 text-sm text-zinc-500">
          Schedule onboarding touchpoints for new FINNies and send them to the scheduling agent.
        </p>
      </div>

      <div className="border-b border-zinc-200">
        <div className="flex gap-0">
          {TABS.map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={cn(
                  'flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors',
                  isActive
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-zinc-500 hover:text-zinc-800 hover:border-zinc-300',
                )}
              >
                <Icon className="h-4 w-4" />
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      <div>{activeTab === 'g2k' && <G2KTab />}</div>
    </div>
  );
}
